import { fmtM } from "@shared-core/domain/format-money";
import { HOT_MAX_WD, LUKEWARM_MAX_WD } from "@shared-core/domain/quote-funnel";
import type { QuoteFunnelBands } from "@shared-core/data/api-types";

// The open-quote book split by freshness (age vs the ~3-week plateau point).
// This IS the win-rate curve rolled up: hot = early on the curve (lots of win rate
// left), cold = past the plateau (exhausted). Forecast-domain pink, with opacity
// as temperature (fade = colder) — the same encoding the win-curve bars use.
const PINK = "#f472b6";

const SEGMENTS = [
  {
    key: "hot" as const,
    label: "🔥 Hot",
    range: `≤ ${HOT_MAX_WD}wd`,
    opacity: 1,
  },
  {
    key: "warm" as const,
    label: "◐ Lukewarm",
    range: `${HOT_MAX_WD + 1}–${LUKEWARM_MAX_WD}wd`,
    opacity: 0.5,
  },
  {
    key: "cold" as const,
    label: "❄ Cold",
    range: `> ${LUKEWARM_MAX_WD}wd`,
    opacity: 0.22,
  },
];

export function FunnelBandsBar({
  bands,
  showLegend = true,
  height = "h-2.5",
}: {
  bands: QuoteFunnelBands;
  showLegend?: boolean;
  height?: string;
}) {
  const total = bands.hot + bands.warm + bands.cold || 1;
  return (
    <div className="space-y-1.5">
      <div className={`flex ${height} w-full overflow-hidden rounded-full`}>
        {SEGMENTS.map((s) => (
          <div
            key={s.key}
            style={{
              width: `${(bands[s.key] / total) * 100}%`,
              background: PINK,
              opacity: s.opacity,
            }}
            title={`${s.label} (${s.range}): ${fmtM(bands[s.key])}`}
          />
        ))}
      </div>
      {showLegend && (
        // Each label centred under its segment (segment-width flex cells; the
        // text may spill past a narrow segment but stays centred on it).
        <div className="flex w-full text-xs text-muted-foreground">
          {SEGMENTS.map((s) => (
            <div
              key={s.key}
              style={{ width: `${(bands[s.key] / total) * 100}%` }}
              className="text-center whitespace-nowrap tabular-nums"
            >
              <div>
                {s.label}{" "}
                <span className="font-medium text-foreground">
                  {fmtM(bands[s.key])}
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground/70">
                {s.range} old
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
